import {
	useMutation,
	UseMutationResult,
	useQuery,
	useQueryClient,
	UseQueryResult,
} from '@tanstack/react-query'
import type { AxiosResponse } from 'axios'

import { PaginatedResponse } from '@/core/api/dtos/common/types'
import { Advert } from '@/core/api/entities/campaign/advert'

import { UpdateAdvertRequestDTO } from '@core/api/dtos/request/advert/update'
import type { AdvertResponseDTO } from '@core/api/dtos/response/campaign/advert'
import { AdvertMapper } from '@core/api/mappers/campaign/advertMapper'
import { advertService } from '@core/api/services/advertService'
import { DEFAULT_PARAMS } from '@core/hooks/common'

type UploadAdvertResponse = AxiosResponse<{ data: AdvertResponseDTO }>

/**
 * Upload an advert (audio file + metadata)
 */
export const useUploadAdvert = ({
	config,
	options,
}: any = {}): UseMutationResult<
	UploadAdvertResponse,
	Error,
	FormData,
	unknown
> => {
	return useMutation<UploadAdvertResponse, Error, FormData, unknown>({
		mutationFn: (formData: FormData) =>
			advertService.uploadAdvert(formData, config),
		...options,
	})
}

/**
 * Paginate adverts for the current account.
 */
export const usePaginateAdverts = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<PaginatedResponse<Advert>, Error> => {
	const queryKey = [
		'adverts',
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey,
		queryFn: () =>
			advertService.paginateAdverts(config).then(({ data: response }) => {
				return {
					data: AdvertMapper.toEntities(response.data),
					meta: response.meta,
				}
			}),
		...options,
	})
}

/**
 * Patch an existing advert's metadata (name, description, start/end dates).
 */
export const usePatchAdvert = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	Advert,
	Error,
	{ advertId: number; data: UpdateAdvertRequestDTO },
	unknown
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		Advert,
		Error,
		{ advertId: number; data: UpdateAdvertRequestDTO },
		unknown
	>({
		mutationFn: ({
			advertId,
			data,
		}: {
			advertId: number
			data: UpdateAdvertRequestDTO
		}) =>
			advertService
				.patchAdvert(advertId, data, config)
				.then(({ data: response }) => AdvertMapper.toEntity(response.data)),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['adverts'] })
			queryClient.invalidateQueries({ queryKey: ['campaign'] })
			queryClient.invalidateQueries({ queryKey: ['campaigns'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}
